import React, { useState } from "react";
import {
    Box,
    TextField,
    IconButton,
    List,
    ListItem,
    Paper,
    Divider
} from "@mui/material";
import { Add, Delete, Edit, Check } from "@mui/icons-material";

interface Props {
    name: string;
    value: string[];
    onChange: (updated: string[]) => void;
    error?: boolean;
}

const TodoListInput: React.FC<Props> = ({ value, onChange, error }) => {
    const [inputValue, setInputValue] = useState("");
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState("");

    // Add a new item
    const handleAdd = () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;

        onChange([...value, trimmed.toLowerCase()]);
        setInputValue("");
    };

    // Delete item
    const handleDelete = (index: number) => {
        const updated = value.filter((_, i) => i !== index);
        onChange(updated);
    };

    // Start editing
    const startEdit = (index: number) => {
        setEditingIndex(index);
        setEditingValue(value[index]);
    };

    // Save edit
    const saveEdit = () => {
        if (editingIndex === null) return;

        const trimmed = editingValue.trim();
        if (!trimmed) {
            // removing empty edits
            handleDelete(editingIndex);
        } else {
            const updated = [...value];
            updated[editingIndex] = trimmed.toLowerCase();
            onChange(updated);
        }
        setEditingIndex(null);
        setEditingValue("");
    };

    // Handle paste multiple values
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text");

        const items = pasted
            .split(/[\n,]+/)
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);

        onChange([...value, ...items]);
    };

    return (
        <Box sx={{ width: "100%" }}>
            {/* Input Row */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Add item"
                    value={inputValue}
                    error={error}
                    // onPaste={handlePaste}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleAdd();
                        }
                    }}
                />
                <IconButton color="primary" onClick={handleAdd}>
                    <Add />
                </IconButton>
            </Box>

            {/* List */}
            <Paper
                variant="outlined"
                sx={{
                    maxHeight: 240,
                    minHeight: 240,
                    overflowY: "auto",
                    borderRadius: 2,
                    p: 1,
                    boxShadow: "none",
                }}
            >
                <List dense disablePadding>
                    {value.map((item, i) => (
                        <React.Fragment key={i}>
                            <ListItem
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    py: 0.5,
                                }}
                            >
                                {/* Inline editing UI */}
                                {editingIndex === i ? (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={editingValue}
                                        autoFocus
                                        onChange={(e) => setEditingValue(e.target.value)}
                                        onBlur={saveEdit}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") saveEdit();
                                            if (e.key === "Escape") setEditingIndex(null);
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            fontSize: "0.95rem",
                                            color: "#1e293b",
                                            cursor: "pointer",
                                            pl: 1,
                                        }}
                                        onClick={() => startEdit(i)}
                                    >
                                        {item}
                                    </Box>
                                )}

                                {/* Action buttons */}
                                {editingIndex === i ? (
                                    <IconButton onClick={saveEdit}>
                                        <Check color="success" />
                                    </IconButton>
                                ) : (
                                    <IconButton onClick={() => startEdit(i)}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                )}

                                <IconButton onClick={() => handleDelete(i)}>
                                    <Delete color="error" fontSize="small" />
                                </IconButton>
                            </ListItem>

                            {i < value.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}

                    {value.length === 0 && (
                        <Box
                            sx={{
                                textAlign: "center",
                                py: 3,
                                fontSize: "0.85rem",
                                color: "text.secondary",
                            }}
                        >
                            No items yet. Start adding above.
                        </Box>
                    )}
                </List>
            </Paper>
        </Box>
    );
};

export default TodoListInput;
